import { useState } from 'react'
import { LayoutGrid, List, Package, ToggleLeft, ToggleRight } from 'lucide-react'
import { cn } from '@trafiflow/shared-ui'
import { ProductChartCard, type ProductDailyData as ChartDailyData } from './ProductChartCard'
import { ConfigurationRequired } from './ConfigurationRequired'
import type { ProductSummary } from '../types'

type ViewMode = 'chart' | 'list'
type NameDisplayMode = 'mapped' | 'raw'

// localStorage key for product name display preference
const PRODUCT_NAME_DISPLAY_KEY = 'trafiflow_product_name_display'

function getProductNameDisplayMode(): NameDisplayMode {
  if (typeof window === 'undefined') return 'mapped'
  const stored = localStorage.getItem(PRODUCT_NAME_DISPLAY_KEY)
  return stored === 'raw' ? 'raw' : 'mapped'
}

function setProductNameDisplayMode(mode: NameDisplayMode): void {
  if (typeof window === 'undefined') return
  localStorage.setItem(PRODUCT_NAME_DISPLAY_KEY, mode)
}

// Transform API daily data to chart component format
function transformDailyDataForChart(product: ProductSummary): ChartDailyData[] {
  if (!product.daily_data || product.daily_data.length === 0) {
    return []
  }
  return product.daily_data.map(d => ({
    date: d.date,
    spend: d.spend,
    roas: d.roas,
    // acqRoas could be added if the API provides it
  }))
}

// Type alias for component props (ProductSummary already has daily_data)
type ProductWithDailyData = ProductSummary

interface ProductsSectionProps {
  products: ProductWithDailyData[] // Chart data (15-day range) - includes daily_data for charts
  listProducts?: ProductWithDailyData[] // List data (single day) - if not provided, uses products
  loading?: boolean
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value)

function getRoasColor(roas: number): string {
  if (roas >= 1) return 'text-green-600'
  return 'text-red-600'
}

function getProductDisplayName(product: ProductWithDailyData, mode: NameDisplayMode): string {
  if (mode === 'raw') {
    // Show first campaign name if available, otherwise fall back to product code
    if (product.campaign_names && product.campaign_names.length > 0) {
      return product.campaign_names[0]
    }
    return product.product_code || product.product || 'Unknown'
  }
  // Show mapped/friendly name
  return product.product || product.product_code || 'Unknown'
}

function ProductsListView({ 
  products, 
  loading, 
  nameDisplayMode 
}: { 
  products: ProductWithDailyData[]; 
  loading?: boolean;
  nameDisplayMode: NameDisplayMode;
}) {
  if (loading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-14 bg-tf-bg-muted rounded-lg animate-pulse" />
        ))}
      </div>
    )
  }

  if (products.length === 0) {
    return (
      <div className="text-center py-12 text-tf-text-muted">
        <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>No product data available</p>
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-tf-bg-muted">
          <tr className="text-left text-xs font-medium text-tf-text-muted uppercase tracking-wider">
            <th className="px-4 py-3">Product</th>
            <th className="px-4 py-3 text-right">Spend</th>
            <th className="px-4 py-3 text-right">Revenue</th>
            <th className="px-4 py-3 text-right">ROAS</th>
            <th className="px-4 py-3 text-right">Campaigns</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {products.slice(0, 15).map((product, index) => (
            <tr key={product.product || index} className="hover:bg-tf-bg-muted transition-colors">
              <td className="px-4 py-3">
                <div className="flex flex-col">
                  <span className="font-medium text-tf-text">
                    {getProductDisplayName(product, nameDisplayMode)}
                  </span>
                  {nameDisplayMode === 'mapped' && product.product_code && (
                    <span className="text-xs text-tf-text-muted">{product.product_code}</span>
                  )}
                  {nameDisplayMode === 'raw' && (
                    <span className="text-xs text-tf-text-muted">
                      {product.product} • {product.campaigns} campaign{product.campaigns !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </td>
              <td className="px-4 py-3 text-right font-medium text-tf-text">
                {formatCurrency(product.spend)}
              </td>
              <td className="px-4 py-3 text-right font-medium text-tf-text">
                {formatCurrency(product.revenue)}
              </td>
              <td className="px-4 py-3 text-right">
                <span className={cn('font-semibold', getRoasColor(product.roas))}>
                  {product.roas.toFixed(2)}x
                </span>
              </td>
              <td className="px-4 py-3 text-right text-tf-text-muted">{product.campaigns}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function ProductsChartView({ 
  products, 
  loading, 
  nameDisplayMode
}: { 
  products: ProductWithDailyData[]; 
  loading?: boolean;
  nameDisplayMode: NameDisplayMode;
}) {
  // Debug: log products to understand data structure
  console.log('[ProductsChartView] Received products:', products.length)
  if (products.length > 0) {
    console.log('[ProductsChartView] First product:', {
      product: products[0].product,
      spend: products[0].spend,
      daily_data: products[0].daily_data,
      daily_data_length: products[0].daily_data?.length || 0
    })
  }

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
          <div key={i} className="h-52 bg-tf-bg-muted rounded-xl animate-pulse" />
        ))}
      </div>
    )
  }

  if (products.length === 0) {
    return (
      <div className="text-center py-12 text-tf-text-muted">
        <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>No product data available</p>
      </div>
    )
  }

  // Sort products by spend and take top 12 that have daily data
  const topProducts = [...products]
    .filter(p => p.daily_data && p.daily_data.length > 0)
    .sort((a, b) => b.spend - a.spend)
    .slice(0, 12)
  
  console.log('[ProductsChartView] Products with daily_data:', topProducts.length)

  // If no products have daily data, show a message
  if (topProducts.length === 0) {
    console.log('[ProductsChartView] No products have daily_data!')
    return (
      <div className="text-center py-12 text-tf-text-muted">
        <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>No daily trend data available for products</p>
        <p className="text-xs mt-2">Try selecting a date range with more data</p>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {topProducts.map((product, index) => (
        <ProductChartCard
          key={product.product || index}
          productName={getProductDisplayName(product, nameDisplayMode)}
          productCode={nameDisplayMode === 'mapped' ? product.product_code : undefined}
          data={transformDailyDataForChart(product)}
        />
      ))}
    </div>
  )
}

// Check if products are properly parsed from campaign names
function areProductsParsedCorrectly(products: ProductWithDailyData[]): boolean {
  if (products.length === 0) return true // No products is valid, not a parsing issue
  
  // Known invalid product names that indicate parsing issues
  const invalidProductNames = ['', 'PPT', 'RMK', 'SHM', 'US', 'CA', 'AU', 'UK', 'ACQ', 'RET', 'VOLUME', 'BIDCAP']
  
  // Count products with valid names
  const validProducts = products.filter(p => {
    const name = p.product?.trim() || ''
    // Product name should be at least 2 chars and not a known invalid name
    return name.length >= 2 && !invalidProductNames.includes(name.toUpperCase())
  })

  // If less than 30% of products have valid names, consider it a parsing issue
  const validRatio = validProducts.length / products.length
  return validRatio >= 0.3
}

export function ProductsSection({ products, listProducts, loading }: ProductsSectionProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('chart')
  const [nameDisplayMode, setNameDisplayMode] = useState<NameDisplayMode>(getProductNameDisplayMode)

  const handleToggleNameDisplay = () => {
    const newMode: NameDisplayMode = nameDisplayMode === 'mapped' ? 'raw' : 'mapped'
    setNameDisplayMode(newMode)
    setProductNameDisplayMode(newMode)
  }

  // Use listProducts for list view (single day), products for chart view (15-day range)
  const effectiveListProducts = listProducts || products

  // Check if we can properly display product data
  const productsAreParsed = areProductsParsedCorrectly(products)

  // If products aren't properly parsed, show configuration message
  if (!loading && !productsAreParsed && products.length > 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Package className="w-5 h-5 text-tf-text-muted" />
          <h3 className="font-semibold text-tf-text">Products</h3>
        </div>
        <ConfigurationRequired
          title="Campaign Naming Convention Not Configured"
          message="We couldn't extract product information from your campaign names. To see product-level analytics, your campaigns need to follow a specific naming convention."
          details={[
            'Position 1: Product code (e.g., 10035)',
            'Position 2: Product name (e.g., SWEETHEART)',
            'Position 3: Layer - PPT (Prospecting), RMK (Remarketing), or SHM (Custom)',
            'Position 4: Country code (e.g., US, CA, AU)',
            'Position 5: Strategy - ACQ (Acquisition) or RET (Retention)',
          ]}
        />
      </div>
    )
  }

  return (
    <div className="bg-tf-bg-card rounded-xl border border-tf-border overflow-hidden">
      {/* Header with view toggle and name display toggle */}
      <div className="px-6 py-4 border-b border-tf-border-sub flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Package className="w-5 h-5 text-tf-text-muted" />
          <h3 className="font-semibold text-tf-text">Products</h3>
          <span className="text-sm text-tf-text-muted">
            ({viewMode === 'chart' ? products.length : effectiveListProducts.length} products)
          </span>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Name Display Toggle */}
          <button
            onClick={handleToggleNameDisplay}
            className={cn(
              'flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg transition-all border',
              nameDisplayMode === 'mapped'
                ? 'bg-tf-feedback-info-bg text-blue-700 border-blue-200'
                : 'bg-tf-bg-muted text-tf-text-muted border-tf-border'
            )}
            title={nameDisplayMode === 'mapped' ? 'Showing friendly names' : 'Showing raw campaign names'}
          >
            {nameDisplayMode === 'mapped' ? (
              <ToggleRight className="w-4 h-4" />
            ) : (
              <ToggleLeft className="w-4 h-4" />
            )}
            {nameDisplayMode === 'mapped' ? 'Friendly Names' : 'Raw Names'}
          </button>

          {/* View Toggle */}
          <div className="flex items-center gap-1 bg-tf-bg-hover rounded-lg p-1">
            <button
              onClick={() => setViewMode('chart')}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-all',
                viewMode === 'chart'
                  ? 'bg-tf-bg-card text-tf-text shadow-sm'
                  : 'text-tf-text-muted hover:text-tf-text-sub'
              )}
            >
              <LayoutGrid className="w-4 h-4" />
              Charts
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-all',
                viewMode === 'list'
                  ? 'bg-tf-bg-card text-tf-text shadow-sm'
                  : 'text-tf-text-muted hover:text-tf-text-sub'
              )}
            >
              <List className="w-4 h-4" />
              List
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {viewMode === 'chart' ? (
          <ProductsChartView 
            products={products} 
            loading={loading} 
            nameDisplayMode={nameDisplayMode}
          />
        ) : (
          <ProductsListView 
            products={effectiveListProducts} 
            loading={loading} 
            nameDisplayMode={nameDisplayMode}
          />
        )}
      </div>
    </div>
  )
}

export default ProductsSection
